import React from 'react';
import { motion } from 'framer-motion';
import { Reveal } from './Placeholder';
import './ProductAbout.css';

const PRINCIPLES = [
  {
    t: 'Conviction, not ego',
    d: 'Lead with a clear view of what the product is and who it’s for. Stay true to what we’ve learned and what our users need, not what any one person wants.',
  },
  {
    t: 'Design is in the details',
    d: 'The timing of an animation, the weight of a tap, the small things are what people actually feel. I’m intentional about every one.',
  },
  {
    t: 'Build quick, learn quick',
    d: 'The fastest way to know is to put it in front of people: a clickable prototype, or the real thing in code, in their hands. Then iterate fast, and trust your team to do what they do best.',
  },
  {
    t: 'AI as a tool',
    d: 'I offload the tedious work to AI so the team can sharpen our workflows and spend our time on the details that actually make the difference.',
  },
];

const TIMELINE = [
  ['May 2023 – Dec 2023', 'Product Design Intern'],
  ['Jan 2024 – Jan 2025', 'Product Lead (Owning Design, Growth & Marketing)'],
  ['Jan 2025 – Present', 'Chief Product Officer'],
];

function ProductAbout() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="ppa-spacer" />

      {/* Intro */}
      <section className="pp-container ppa-intro">
        <div className="ppa-intro__head">
          <Reveal>
            <span className="pp-eyebrow">About</span>
            <h1 className="ppa-title">
              A designer who became a product leader.<br />
              <em>And never stopped designing.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.14} className="ppa-intro__portrait">
            <img className="ppa-portrait-img" src="/images/about/Portfolio-Portait.png" alt="Samantha de Nicola" />
          </Reveal>
        </div>

        <div className="ppa-bio">
          <Reveal className="ppa-bio__row">
            <span className="ppa-bio__label">Background</span>
            <p className="ppa-bio__text">
              I’m Samantha de Nicola, born and raised in LA, and a product of USC’s Iovine and Young
              Academy, a degree built at the intersection of design, technology, and business (with
              specializations in computer programming and the application of artificial intelligence).
              Previously Meta. For the past three years I’ve put that whole toolkit to work as the
              sole designer turned CPO of a music-tech startup.
            </p>
          </Reveal>
          <Reveal delay={0.05} className="ppa-bio__row">
            <span className="ppa-bio__label">Why I design</span>
            <p className="ppa-bio__text">
              The small moments that pull someone into an experience (digital or live) are the same
              ones that pull me into design: making products people can touch, where the timing of an
              animation or the weight of a tap is what makes it land. It comes from a deep love of
              music and live shows. I’m obsessed with the details that make a concert, an interactive
              experience, even an escape room <em>feel</em> like something.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="ppa-bio__row">
            <span className="ppa-bio__label">Off the clock</span>
            <p className="ppa-bio__text">
              I can’t turn the maker instinct off. I get an idea and I have to build it, whether it’s
              a side-project app, a small tool, or something for a friend or family member who needs it.
              When I’m not building, I’m probably in a Pilates class, at the beach, reading, or at a
              live show in LA.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="ppa-bio__row">
            <span className="ppa-bio__label">What’s next</span>
            <p className="ppa-bio__text">
              I’m always taking consumer apps apart: how Beli stays honest and dead-on for its users,
              how Rodeo shipped a rough MVP and kept learning its way to the right product. That’s the
              work I want to do next: lead with craft, carry the product thinking behind it, and own
              something end to end, whether that’s an early-stage startup or a new product bet inside
              a bigger company.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Principles */}
      <section className="ppa-principles">
        <div className="pp-container">
          <Reveal className="pp-section-head">
            <div>
              <span className="pp-eyebrow">How I think</span>
              <h2 className="pp-section-title">Four things I believe about building product.</h2>
            </div>
          </Reveal>
          <div className="ppa-principles__grid">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.05} className="ppa-principle">
                <span className="ppa-principle__num">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="ppa-principle__t">{p.t}</h3>
                <p className="ppa-principle__d">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="pp-container ppa-timeline-wrap">
        <Reveal className="pp-section-head">
          <div>
            <span className="pp-eyebrow">The arc</span>
            <h2 className="pp-section-title">Intern → CPO, in three years.</h2>
          </div>
        </Reveal>
        <div className="ppa-timeline">
          {TIMELINE.map(([year, label], i) => (
            <Reveal key={i} delay={i * 0.05} className="ppa-tl-row">
              <span className="ppa-tl-year">{year}</span>
              <span className="ppa-tl-dot" />
              <span className="ppa-tl-label">{label}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="ppa-cta">
        <div className="pp-container pp-container--narrow">
          <Reveal className="ppa-cta__inner">
            <h2 className="ppa-cta__title">Want the long version?</h2>
            <p className="ppa-cta__body">Happy to walk through any of it: the craft, the operating story, or the messy middle.</p>
            <div className="ppa-cta__actions">
              <a href="mailto:sami@samidenicola.com" className="pp-btn pp-btn--solid">sami@samidenicola.com</a>
              <a href="https://www.linkedin.com/in/samidenicola/" target="_blank" rel="noopener noreferrer" className="pp-btn pp-btn--ghost">LinkedIn ↗</a>
            </div>
          </Reveal>
        </div>
      </section>
    </motion.div>
  );
}

export default ProductAbout;
